import { useState } from "react";
import { toast } from "sonner";
import { Coins, Utensils, Egg } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, formatApiErrorDetail } from "@/lib/api";
import { familiarStage, FAMILIAR_MEAL_COST, getClass } from "@/lib/rpg";
import { card, label, btnPrimary, input } from "@/lib/styles";

export default function Familiar() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user) return null;

  const fam = user.familiar;
  const gold = user.gold ?? 0;
  const klass = getClass(user.character_class);

  const adopt = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/familiar/adopt", { name: name.trim() });
      setUser(data);
      toast.success(`${name.trim()} has hatched!`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const feed = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/familiar/feed");
      setUser(data.user);
      toast.success(`Fed ${data.user.familiar.name} · −${data.cost} gold`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const stage = fam ? familiarStage(fam.meals || 0) : null;

  return (
    <div className="min-h-screen">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between">
          <span className={label}>your familiar</span>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-gold border border-gold/30 bg-gold/5 px-2 py-1 rounded-md">
            <Coins size={13} /> your gold · {gold}
          </span>
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">The Daemon</h1>
        <p className="text-sm text-zinc-500 mt-1">A background process that grows the more gold you pour into it.</p>

        {!fam ? (
          <div className={`${card} p-8 mt-8 text-center`}>
            <Egg className="mx-auto text-gold" size={40} />
            <p className="mt-3 text-zinc-300">You have no familiar. Adopt one: it starts as an egg and evolves as you feed it.</p>
            <div className="mt-5 flex gap-2 max-w-sm mx-auto">
              <input
                data-testid="familiar-name"
                className={input}
                placeholder="Name your daemon…"
                value={name}
                maxLength={24}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && adopt()}
              />
              <button data-testid="familiar-adopt" onClick={adopt} disabled={busy || !name.trim()} className={btnPrimary}>
                Adopt
              </button>
            </div>
          </div>
        ) : (
          <div className={`${card} p-8 mt-8 text-center`}>
            <pre className={`font-mono text-2xl leading-tight whitespace-pre ${klass?.tint || "text-gold-light"}`} data-testid="familiar-art">
{stage.art}
            </pre>
            <p className="mt-4 font-serif font-bold text-xl text-zinc-100">{fam.name}</p>
            <p className={`text-[11px] uppercase tracking-[0.28em] mt-1 ${klass?.tint || "text-gold"}`}>{stage.name} · {fam.meals || 0} meals</p>

            <div className="mt-5 max-w-xs mx-auto">
              {stage.isMax ? (
                <p className="text-xs text-gold-light">Fully evolved: a true Oracle.</p>
              ) : (
                <p className="text-xs text-zinc-500">{stage.toNext} more meal{stage.toNext === 1 ? "" : "s"} until it evolves.</p>
              )}
            </div>

            <button
              data-testid="familiar-feed"
              onClick={feed}
              disabled={busy || gold < FAMILIAR_MEAL_COST}
              className={`${btnPrimary} mt-6`}
            >
              <Utensils size={16} /> Feed · −{FAMILIAR_MEAL_COST} gold
            </button>
            {gold < FAMILIAR_MEAL_COST && <p className="text-[11px] text-rose-400/80 mt-2">Not enough gold. Clear a trial to earn more.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
